<script setup>
import {computed, ref} from "vue";
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const props = defineProps(['periodData'])

const search = ref('')

const attendanceHeaders = [
  {title: 'Login', key: 'login', width: "50%"},
  {title: t('gamification.attendance_date'), key: 'createdAt', width: "50%", sortable: false}
]
const evaluationHeaders = [
  {title: t('gamification.star_count'), key: 'rating', width: "30%"},
  {title: t('gamification.rating'), key: 'text', width: "70%"}
]
const testResultHeaders = [
  {title: 'Login', key: 'login', width: "20%"},
  {title: t('gamification.fullname'), key: 'fullName', width: "30%"},
  {title: t('gamification.test_name'), key: 'testName', width: "25%"},
  {title: t('gamification.score'), key: 'score', width: "10%"},
  {title: t('gamification.test_date'), key: 'date', width: "15%"}
]

const evaluations = computed(()=> props.periodData.evaluations ?? [])

const ratingAverage = computed(()=>{
  if(evaluations.value.length === 0){ return 0 }
  const sum = evaluations.value.reduce((acc, e) => acc + e.rating, 0)
  return (sum / evaluations.value.length).toFixed(1)
})

function formatDate(value) {
  return new Date(value).toLocaleString("cs-CZ", {
    hour: "2-digit",
    minute: "2-digit",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}
</script>

<template>
  <div class="pt-5 px-5">
    <v-alert type="info" variant="tonal" density="compact" class="mb-5">
      {{ t('gamification.history_read_only') }}
    </v-alert>

    <h2>{{ t('gamification.attendance') }}</h2>
    <v-text-field
        v-model="search"
        prepend-icon="search"
        variant="underlined"
        :placeholder="t('gamification.search_user')"
        class="w-50 pt-2"
    />
    <v-data-table
        :search="search"
        :headers="attendanceHeaders"
        :items="periodData.attendances ?? []"
        :no-data-text="t('gamification.no_data')"
    >
      <template v-slot:item.createdAt="{ value }">
        {{ formatDate(value) }}
      </template>
    </v-data-table>

    <div class="d-flex ga-2 align-end pt-5">
      <h2>{{ t('gamification.evaluation') }}</h2>
      <v-chip variant="flat" label append-icon="star" color="grey-lighten-4">
        {{ ratingAverage }}
      </v-chip>
      <v-chip variant="flat" label color="grey-lighten-4">
        {{ evaluations.length }} {{ t('gamification.users_genitive') }}
      </v-chip>
    </div>
    <v-data-table
        class="mt-2"
        :headers="evaluationHeaders"
        :items="evaluations"
        :no-data-text="t('gamification.no_data')"
    >
      <template v-slot:item.rating="{ value }">
        <div class="d-flex ga-2">
          <v-icon v-for="n in Number(value)" :key="n" icon="star" color="yellow-darken-1" />
        </div>
      </template>
    </v-data-table>

    <h2 class="pt-5">{{ t('gamification.test_results') }}</h2>
    <v-data-table
        class="mt-2"
        :headers="testResultHeaders"
        :items="periodData.testResults ?? []"
        :no-data-text="t('gamification.no_data')"
    >
      <template v-slot:item.score="{ item }">
        {{ item.score }} / {{ item.maxPoints }}
      </template>
      <template v-slot:item.date="{ value }">
        {{ formatDate(value) }}
      </template>
    </v-data-table>
  </div>
</template>

<style scoped>
.v-chip :deep(.v-chip__append .v-icon) {
  color: #fdd835;
}
</style>
